import { useQueryClient } from "@tanstack/react-query";
import { api, extractErrorMessage } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import { pismena } from "@/lib/dielecKod";
import type {
  BulkDielceCreateResp,
  DataResponse,
  Dielec,
  DielecCreate,
  Zostava,
} from "@/api/schemas";

/** Čo prešlo a čo nie — hromadná akcia sa pri chybe jednej nezastaví. */
export interface VysledokHromadne {
  hotovo: string[];
  chyby: { kod: string; sprava: string }[];
}

/**
 * Akcie na viac vybraných zostáv naraz (KOLO 196) — user: *„nech Ctrl
 * funguje aj pri zostavách… vybrať zostavy a vložiť do nich rovnaké
 * kovanie"*.
 *
 * Volá tie isté endpointy ako jednotlivá akcia, postupne (backend po
 * každej prečísluje kódy), ale **krok späť je jeden** na celú akciu —
 * pri piatich zostavách by inak Ctrl+Z vracal po jednej. Preto priamo cez
 * `api`, nie cez hooky, ktoré si krok zapisujú samy.
 */
export function useHromadneZostavy(zakazkaId: string): {
  pridajDielce: (
    polozky: { zostavaId: string; kod: string; data: DielecCreate }[],
    popis: string,
  ) => Promise<VysledokHromadne>;
  pridajDielceNaraz: (
    polozky: { zostavaId: string; kod: string; dielce: DielecCreate[] }[],
    popis: string,
  ) => Promise<VysledokHromadne>;
  duplikuj: (zostavy: Zostava[], vsetky: Zostava[]) => Promise<VysledokHromadne>;
  zmaz: (zostavy: Zostava[]) => Promise<VysledokHromadne>;
} {
  const qc = useQueryClient();
  const undo = useUndoOptional();

  async function obnov(): Promise<void> {
    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) }),
      qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() }),
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() }),
    ]);
  }

  async function pridajDielce(
    polozky: { zostavaId: string; kod: string; data: DielecCreate }[],
    popis: string,
  ): Promise<VysledokHromadne> {
    const vysledok: VysledokHromadne = { hotovo: [], chyby: [] };
    for (const p of polozky) {
      try {
        const { data } = await api.post<DataResponse<Dielec>>(
          `/zostavy/${p.zostavaId}/dielce`,
          p.data,
        );
        vysledok.hotovo.push(data.data.id);
      } catch (err) {
        vysledok.chyby.push({ kod: p.kod, sprava: extractErrorMessage(err) });
      }
    }
    const nove = [...vysledok.hotovo];
    if (nove.length > 0) {
      undo?.pridaj({
        popis,
        vrat: async () => {
          for (const id of nove) await api.delete(`/dielce/${id}`);
          await obnov();
        },
      });
    }
    await obnov();
    return vysledok;
  }

  /**
   * Viac riadkov do zostavy jednou požiadavkou (`/dielce/bulk`, KOLO 226 —
   * user: „viacero kovaní naraz vložiť do zostavy, nie len jedno"). Server
   * ich založí v jednej transakcii a prečísluje raz; späť je jeden krok
   * pre všetky zostavy.
   */
  async function pridajDielceNaraz(
    polozky: { zostavaId: string; kod: string; dielce: DielecCreate[] }[],
    popis: string,
  ): Promise<VysledokHromadne> {
    const vysledok: VysledokHromadne = { hotovo: [], chyby: [] };
    for (const p of polozky) {
      try {
        const { data } = await api.post<DataResponse<BulkDielceCreateResp>>(
          `/zostavy/${p.zostavaId}/dielce/bulk`,
          { dielce: p.dielce },
        );
        vysledok.hotovo.push(...data.data.dielce.map((d) => d.id));
      } catch (err) {
        vysledok.chyby.push({ kod: p.kod, sprava: extractErrorMessage(err) });
      }
    }
    const nove = [...vysledok.hotovo];
    if (nove.length > 0) {
      undo?.pridaj({
        popis,
        vrat: async () => {
          for (const id of nove) await api.delete(`/dielce/${id}`);
          await obnov();
        },
      });
    }
    await obnov();
    return vysledok;
  }

  async function duplikuj(
    zostavy: Zostava[],
    vsetky: Zostava[],
  ): Promise<VysledokHromadne> {
    const vysledok: VysledokHromadne = { hotovo: [], chyby: [] };
    for (const z of zostavy) {
      try {
        // Kópia ide hneď za zdroj — kód dorovná backend (KOLO 134)
        const index = vsetky.findIndex((x) => x.id === z.id);
        const { data } = await api.post<DataResponse<Zostava>>(
          `/zostavy/${z.id}/duplicate`,
          { new_kod: pismena(index + 1), new_nazov: `${z.nazov} (kópia)` },
        );
        vysledok.hotovo.push(data.data.id);
      } catch (err) {
        vysledok.chyby.push({ kod: z.kod, sprava: extractErrorMessage(err) });
      }
    }
    const kopie = [...vysledok.hotovo];
    if (kopie.length > 0) {
      undo?.pridaj({
        popis: `duplikovanie ${kopie.length} zostáv`,
        vrat: async () => {
          for (const id of kopie) await api.delete(`/zostavy/${id}`);
          await obnov();
        },
      });
    }
    await obnov();
    return vysledok;
  }

  async function zmaz(zostavy: Zostava[]): Promise<VysledokHromadne> {
    const vysledok: VysledokHromadne = { hotovo: [], chyby: [] };
    for (const z of zostavy) {
      try {
        await api.delete(`/zostavy/${z.id}`);
        vysledok.hotovo.push(z.id);
      } catch (err) {
        vysledok.chyby.push({ kod: z.kod, sprava: extractErrorMessage(err) });
      }
    }
    const zmazane = [...vysledok.hotovo];
    if (zmazane.length > 0) {
      undo?.pridaj({
        popis: `zmazanie ${zmazane.length} zostáv`,
        vrat: async () => {
          // Obnovenie vráti aj dielce zmazané spolu so zostavou
          for (const id of zmazane) await api.post(`/zostavy/${id}/obnovit`);
          await obnov();
        },
      });
    }
    await obnov();
    return vysledok;
  }

  return { pridajDielce, pridajDielceNaraz, duplikuj, zmaz };
}
